import React from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { SupabaseUpload } from '../../../upload/SupabaseUpload';
import InterviewMedia from '@/components/interview/InterviewMedia';
import { EnhancedLabel, EnhancedPopoverTrigger } from '@/lib/shadcn-wrappers';

const PersonalInfoSection = ({ form }) => {
  console.log('[CRITICAL PERSONALINFO] PersonalInfoSection rendered - MUST APPEAR IN CONSOLE');
  const photoUrl = form.watch('photoUrl');
  const photoUrl2 = form.watch('photoUrl2');
  const intervieweeName = form.watch('intervieweeName');
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [displayDate, setDisplayDate] = React.useState('');
  
  const [displayTime, setDisplayTime] = React.useState('');

  // Initialize display date and time from form value
  React.useEffect(() => {
    const currentValue = form.getValues('timestamp');
    if (currentValue) {
      const d = new Date(currentValue);
      if (!displayDate) setDisplayDate(format(d, 'dd.MM.yyyy'));
      if (!displayTime) setDisplayTime(format(d, 'HH:mm'));
    }
  }, [form.getValues('timestamp')]);

  return (
    <div className="border rounded-md p-4 pb-12 mb-8">
      <h3 className="text-lg font-semibold mb-4">Personopplysninger</h3>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="timestamp"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <EnhancedLabel>Publikasjonsdato</EnhancedLabel>
              <div className="flex">
                <FormControl>
                  <Input
                    placeholder="dd.mm.yyyy"
                    value={displayDate}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setDisplayDate(inputValue);
                      console.log('[PersonalInfoSection] Date input changed:', inputValue);
                      
                      // Try to parse the date when it matches the complete format
                      if (inputValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        const [day, month, year] = inputValue.split('.').map(Number);
                        const date = new Date(year, month - 1, day);
                        
                        if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
                          // Preserve existing time from displayTime or use noon
                          if (displayTime.match(/^\d{2}:\d{2}$/)) {
                            const [h, m] = displayTime.split(':').map(Number);
                            date.setHours(h, m, 0, 0);
                          } else if (field.value) {
                            const existingDate = new Date(field.value);
                            date.setHours(existingDate.getHours(), existingDate.getMinutes(), existingDate.getSeconds(), existingDate.getMilliseconds());
                          } else {
                            date.setHours(12, 0, 0, 0);
                          }
                          const isoDate = date.toISOString();
                          console.log('[PersonalInfoSection] Setting new timestamp:', isoDate);
                          field.onChange(isoDate);
                        }
                      }
                    }}
                    className="rounded-r-none"
                  />
                </FormControl>
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <EnhancedPopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-l-none border-l-0"
                      type="button"
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </EnhancedPopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Update display date
                            setDisplayDate(format(date, 'dd.MM.yyyy'));
                            
                            // Preserve the time from the existing timestamp, or use noon as default
                            if (field.value) {
                              const existingDate = new Date(field.value);
                              date.setHours(existingDate.getHours());
                              date.setMinutes(existingDate.getMinutes());
                              date.setSeconds(existingDate.getSeconds());
                              date.setMilliseconds(existingDate.getMilliseconds());
                            } else {
                              // Use noon as default time for new dates
                              date.setHours(12, 0, 0, 0);
                            }
                            const newTimestamp = date.toISOString();
                            console.log('[PersonalInfoSection] Calendar date selected:', newTimestamp);
                            field.onChange(newTimestamp);
                            setIsCalendarOpen(false); // Close calendar after selection
                          }
                        }}
                      locale={nb}
                      captionLayout="dropdown"
                      fromYear={1900}
                      toYear={2100}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <Input
                  type="time"
                  value={displayTime}
                  onChange={(e) => {
                    const timeValue = e.target.value;
                    setDisplayTime(timeValue);
                    if (timeValue.match(/^\d{2}:\d{2}$/) && field.value) {
                      const [hours, minutes] = timeValue.split(':').map(Number);
                      const date = new Date(field.value);
                      date.setHours(hours, minutes, 0, 0);
                      field.onChange(date.toISOString());
                    }
                  }}
                  className="w-24 ml-2"
                  placeholder="HH:mm"
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="intervieweeName"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Navn på intervjuobjekt</EnhancedLabel>
              <FormControl>
                <Input placeholder="Fullt navn" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Yrkestittel</EnhancedLabel>
              <FormControl>
                <Input placeholder="Stilling eller tittel" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Firma</EnhancedLabel>
              <FormControl>
                <Input placeholder="Selskap eller organisasjon" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="photographerCredit"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Kreditering fotograf hvis aktuelt</EnhancedLabel>
              <FormControl>
                <Input placeholder="F.eks. Foto: John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Profilbilde 1</label>
            <SupabaseUpload
              bucket="photoalbum"
              onUploaded={(url) => {
                if (url.startsWith('blob:')) {
                  console.error('[EditorUpload] Blob URL detected – aborting', url);
                  return;
                }
                console.log('[PersonalInfoSection] SupabaseUpload callback with URL:', url);
                form.setValue('photoUrl', url, { shouldDirty: true, shouldTouch: true });
              }}
              initialImage={photoUrl || undefined}
            />
          </div>
          <div className="pt-2">
            <p className="text-sm text-muted-foreground mb-2">Forhåndsvisning</p>
            <InterviewMedia 
              intervieweeName={intervieweeName || 'Ukjent'} 
              photoUrl={photoUrl || undefined}
              className="w-24 h-24"
            />
          </div>
        </div>
        
        {/* Second profile picture - optional */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-dashed border-muted">
          <div>
            <label className="block text-sm font-medium mb-2">
              Profilbilde 2 
              <span className="text-xs text-muted-foreground font-normal ml-2">
                (Valgfritt - for artikler med to intervjuobjekter)
              </span>
            </label>
            <SupabaseUpload
              bucket="photoalbum"
              onUploaded={(url) => {
                if (url.startsWith('blob:')) {
                  console.error('[EditorUpload] Blob URL detected – aborting', url);
                  return;
                }
                console.log('[PersonalInfoSection] SupabaseUpload callback for photoUrl2:', url);
                form.setValue('photoUrl2', url, { shouldDirty: true, shouldTouch: true });
              }}
              initialImage={photoUrl2 || undefined}
            />
          </div>
          <div className="pt-2">
            <p className="text-sm text-muted-foreground mb-2">Forhåndsvisning bilde 2</p>
            {photoUrl2 ? (
              <InterviewMedia 
                intervieweeName={intervieweeName || 'Ukjent'} 
                photoUrl={photoUrl2}
                className="w-24 h-24"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">
                Ikke valgt
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;
